import { useEffect, useRef, useState } from "react";

import { usePersistedForm } from "../contexts/formPersistContext";

export type FieldControl<Item> = {
  value: Item;
  setValue: (v: Item) => void;
  status: "default" | "error";
  error?: string;
  onBlur: () => void;
};

export type UseFormReturn<Form> = {
  field<Field extends keyof Form>(k: Field): FieldControl<Form[Field]>;
  handleSubmit: (
    submit: (value: Form) => void | Promise<void>,
    refreshValuesAfter?: boolean,
  ) => () => Promise<void>;
  valid: boolean;
  submitting: boolean;
  formValues: Form;
  validateAll: () => void;
  persistFormValues: () => void;
};

type State<Form> = {
  formValues: Form;
  firstValidationFinished: boolean;
  touched: Record<keyof Form, boolean | undefined>;
  error: Record<keyof Form, string | undefined>;
};

export type FormValidator<Form> = {
  [k in keyof Form]?: (
    v: Form[k],
    form: Omit<Form, k>,
  ) => (string | undefined) | Promise<string | undefined>;
};

export type UseFormConfig<Form> = {
  initValue: Form;
  validators: FormValidator<Form>;
  persistentKey?: string;
};

export function useForm<Form>({
  initValue,
  validators,
  persistentKey,
}: UseFormConfig<Form>): UseFormReturn<Form> {
  const { persistForm, getPersistedForm } = usePersistedForm();
  const [state, setState] = useState<State<Form>>({
    formValues: (persistentKey && getPersistedForm(persistentKey)) || initValue,
    firstValidationFinished: false,
    // @ts-ignore
    touched: {},
    // @ts-ignore
    error: {},
    // @ts-ignore
    watchers: {},
  });
  const [submitting, setSubmitting] = useState(false);
  const currentStateRef = useRef<State<Form>>();
  currentStateRef.current = state;

  const validateAll = async () => {
    const validatedState = { ...state };
    for (const key in state.formValues) {
      const validate = validators[key];
      if (validate) {
        validatedState.error[key] = await validate(
          state.formValues[key],
          state.formValues,
        );
      }
    }

    setState((oldState) => ({
      ...oldState,
      ...validatedState,
      firstValidationFinished: true,
    }));
  };

  const isFormValid = () => {
    for (const key in state.error) {
      if (state.error[key]) {
        return false;
      }
    }
    return true;
  };

  async function validate<Field extends keyof Form>(
    field: Field,
    v: Form[Field],
  ) {
    const validate = validators[field];
    if (!validate) return;
    const err = await validate(
      v,
      currentStateRef.current?.formValues || state.formValues,
    );
    setState((oldState) => {
      if (v === oldState.formValues[field]) {
        return {
          ...oldState,
          error: { ...oldState.error, [field]: err },
        };
      } else {
        return oldState;
      }
    });
  }

  const persistFormValues = () => {
    if (persistentKey && currentStateRef.current) {
      persistForm(persistentKey, { ...currentStateRef.current.formValues });
    }
  };

  useEffect(() => {
    validateAll();

    return persistFormValues;
  }, []);

  return {
    formValues: state.formValues,
    persistFormValues,
    submitting,
    validateAll,
    valid: isFormValid(),
    handleSubmit: (submit, refreshValuesAfter) => async () => {
      if (!isFormValid() && state.firstValidationFinished) return;
      setSubmitting(true);
      await submit(state.formValues);
      setSubmitting(false);
      if (refreshValuesAfter) {
        setState({
          firstValidationFinished: false,
          formValues: initValue,
          // @ts-ignore
          touched: {},
          // @ts-ignore
          error: {},
          // @ts-ignore
          watchers: {},
        });
      }
    },
    field(field) {
      const error = state.error[field];
      const touched = state.touched[field];
      return {
        value: state.formValues[field],
        setValue: (v) => {
          setState((oldState) => ({
            ...oldState,
            formValues: {
              ...oldState.formValues,
              [field]: v,
            },
          }));

          validate(field, v);
        },
        onBlur: () => {
          setState((oldState) => ({
            ...oldState,
            touched: {
              ...oldState.touched,
              [field]: true,
            },
          }));
          validate(
            field,
            (currentStateRef.current?.formValues || state.formValues)[field],
          );
        },
        status:
          touched && error !== null && error !== undefined
            ? "error"
            : "default",
        error: touched && error,
      };
    },
  };
}
